/* eslint-disable max-lines */
import {Box, Divider, Flex, Icon, IconWithText, P} from 'exsportia-components'
// import {TextType} from 'exsportia-components/dist/types'
import {useFormikContext} from 'formik'
import {DateTime} from 'luxon'
import React, {FC, useCallback, useEffect, useMemo} from 'react'
import {useMutation, useQuery} from 'react-query'
import {useParams} from 'react-router-dom'

import {BookingQueries} from '../../api/booking'
import {LimitationCheckResultType, LimitationCheckResultTypeKey} from '../../api/booking/calculate-booking-price'
import {CalculateSplitBookingPriceQueryParams} from '../../api/booking/calculate-split-booking-price'
import {GetBookingQueryReturnType} from '../../api/booking/get-booking'
import {BookingUsageDataType} from '../../api/booking/types'
import {MemberQueries} from '../../api/member'
import {MemberParticipantType} from '../../api/member/types'
import {SubscriptionsQueries} from '../../api/subscriptions'
import {Subscription} from '../../api/subscriptions/types'
import {VenueQueries} from '../../api/venue'
import {PricingDetails, PricingElementType} from '../../components/pricing-details/pricing-details'
import {SubscriptionCard} from '../../components/subscription-card/subscription-card'
import {ParticipantsList} from '../../components/timeslot-grid/components/participants-list'
import {FULL_CANCEL_POLICY_OPTIONS_MAP} from '../../constants/cancel-policy'
import {USAGE_TYPE_SUBSCRIPTION_OPTIONS_LABEL_MAP} from '../../constants/usage-type'
import {getParticipantsEquipment} from '../../helpers/data'
import {formatOpeningTimes} from '../../helpers/pass-hours'
import {usePassHoursOptions} from '../../helpers/use-pass-hours-options'
import {useAuthState} from '../../services/auth/hooks'
import {CreateServiceBookingFormType} from '../../services/booking/forms/create-service-booking-form'
import {EditParticipantForm, EditParticipantFormProps} from '../../services/booking/forms/edit-participant-form'
import {BookingStateParticipant} from '../../services/booking/forms/types'
import {useBookingLoader} from '../../services/booking/hooks'
import {useServiceOptions} from '../../services/service-options/hooks'
import {UserService} from '../../services/user'
import {Button} from '../../ui/components/button/button'
import {Chip} from '../../ui/components/chip/chip'
import {LabeledValueRow} from '../../ui/components/key-value-row/labeled-value-row'
import {LabeledSection} from '../../ui/components/labeled-section/labeled-section'
import {TitledSection} from '../../ui/components/titeled-section/titled-section'
import {auth} from '../../utils/firebase'
import {LocaleOptions, useLocale, useLuxon} from '../../utils/i18n'
import {useModalActions} from '../../utils/modals'
import {NO_USER_PHOTO} from '../../utils/remote-content'
import {AreaSection} from '../booking-profile/components/area-section'
import {InstructorSection} from '../booking-profile/components/instructor-section'
import {PaySplitPaymentServiceBookingFormType} from '../pay-split-payment-booking/pay-split-payment-booking'
import {EditParticipantModal} from '../set-participants/components/edit-participant-modal'

const defaultLOptions: LocaleOptions = {caseAction: 'capitalize'}

/* eslint-disable max-lines-per-function */
export const BookingServiceSummarySection: FC<{
  values: PaySplitPaymentServiceBookingFormType
  booking: GetBookingQueryReturnType
  isLoading: boolean
}> = ({values, booking, isLoading}) => {
  const {l, language} = useLocale()
  const luxon = useLuxon()
  const params = useParams<{venueId: string}>()
  const {openModal} = useModalActions()
  if (!booking || isLoading) {
    return null
  }
  const isJustTheSpace = booking.areaSpace || false
  const {data: venueData} = useQuery(
    VenueQueries.getVenueQuery({
      params: {id: params.venueId},
    }),
  )
  const {data, isFetching, refetch} = useQuery(BookingQueries.calculateSplitBookingPriceQuery({enabled: false, refetchOnWindowFocus: false}))
  useEffect(() => {
    if (venueData?.uid && auth.currentUser?.uid) {
      refetch()
    }
  }, [venueData?.uid, auth.currentUser?.uid, values.splitPayment, values.participants])
  const {getServiceConfigByServiceType} = useServiceOptions([booking.sportType])
  // useEffect(() => {
  //   setIsLoading(isFetching)
  // }, [isFetching, setIsLoading])
  // useEffect(() => {
  //   setWholePrice && setWholePrice(data?.[0].price)
  // }, [isFetching, data, setWholePrice])
  const {bookingDateTime, bookingDuration} = useMemo<{
    bookingDateTime: DateTime
    bookingDuration: string
  }>(() => {
    if (!booking.date_timestamp) {
      return {bookingDateTime: luxon.now(), bookingDuration: '-'}
    }
    const startTime = Number(booking.date_timestamp)
    return {
      bookingDateTime: luxon.fromMillis(startTime),
      bookingDuration:
        booking.duration === 'NO_DURATION'
          ? l('noDuration')
          : `${booking.duration} ${l('min')}`,
    }
  }, [l, luxon, booking.date_timestamp])
  const {icon} = useMemo(
    () => getServiceConfigByServiceType(booking.sportType),
    [booking, getServiceConfigByServiceType],
  )
  const cancelPolicyToUse = booking.cancelPolicy
  const cancelPolicyKey = cancelPolicyToUse && FULL_CANCEL_POLICY_OPTIONS_MAP[cancelPolicyToUse]?.description
  const cancelPolicyDescription = cancelPolicyKey ? l(cancelPolicyKey) : cancelPolicyToUse
  const usageData: unknown = useMemo(
    () =>
      data
        ? data.participants.reduce(
            (acc, participant) => {
              const used: LimitationCheckResultType | null = participant.limitationCheckResult?.used || null
              if (!used) {
                return acc
              }
              const firstPropName: LimitationCheckResultTypeKey | null = Object.keys(
                used,
              )[0] as LimitationCheckResultTypeKey
              return {
                used: acc.used + (used[firstPropName] || 0),
                amountByType: USAGE_TYPE_SUBSCRIPTION_OPTIONS_LABEL_MAP[firstPropName],
              }
            },
            {
              used: 0,
              amountByType: USAGE_TYPE_SUBSCRIPTION_OPTIONS_LABEL_MAP.entries,
            },
          )
        : null,
    [data],
  )
  const aboutChildrenArray = useMemo(() => {
      const arrayToReturn: React.ReactNode[] = []
      if (booking.areaId) {
        arrayToReturn.push(<AreaSection key='area' areaId={booking.areaId} />)
      }
      if (booking.teacherId) {
        arrayToReturn.push(
          <InstructorSection key='instructor' teacherId={booking.teacherId} />,
        )
      }
    if (values.participants.length > 0) {
      const firstParticipant: BookingStateParticipant | undefined =
        values.participants[0]
      if (firstParticipant.membership) {
        arrayToReturn.push(
          <TitledSection key='subscription' title={l('subscription')}>
            <SubscriptionCard
              usage={usageData as BookingUsageDataType}
              styleType='summary'
              isLoading={isFetching}
              name={firstParticipant.membership.name}
              imageUrl={firstParticipant.membership.logo?.secure_url || ''}
            />
          </TitledSection>,
        )
      }
    }

    return arrayToReturn
  }, [booking, language])

  const openParticipantsModal = useCallback(() => {
    openModal({
      title: l('participants'),
      footerConfig: {
        disabled: true,
      },
      component: (
        <ParticipantsList
          participants={values.participants}
          displayEquipmentCount={true}
        />
      ),
    })
  }, [values.participants, openModal])

  return (
    <Box>
      <Chip
        text={booking.name}
        icon={icon}
        styleType={!isJustTheSpace ? 'service' : 'justTheSpace'}
      />
      <TitledSection title={l('booking_information', defaultLOptions)}>
        <LabeledValueRow label={l('dateAndTime')}>
          {bookingDateTime.toUTC().toFormat('HH:mm, MMM dd, yyyy')}
        </LabeledValueRow>
        <LabeledValueRow label={l('duration')}>{bookingDuration}</LabeledValueRow>
        <LabeledValueRow label={l('participants')}>
          <Flex
            cursor='pointer'
            onClick={openParticipantsModal}
          >
            {`${booking.participants.length} ${l('participants')}`}
          </Flex>
        </LabeledValueRow>
      </TitledSection>
      <TitledSection title={l('about')}>{aboutChildrenArray}</TitledSection>
      {values.participants[0]?.membership && (
        <TitledSection title={l('subscription')}>
          <SubscriptionCard
            usage={usageData as BookingUsageDataType}
            styleType='summary'
            isLoading={false}
            name={values.participants[0].membership.name}
            imageUrl={values.participants[0].membership.logo?.secure_url || ''}
          />
        </TitledSection>
      )}
      <TitledSection title={l('extra_details', defaultLOptions)}>
        {cancelPolicyDescription && (
          <Box pt={'8px'}>
            <LabeledSection label={l('cancelPolicy')}>{cancelPolicyDescription}</LabeledSection>
          </Box>
        )}
      </TitledSection>
    </Box>
  )
}

export const BookingServicePricingAndPaymentsSection: FC<{
  booking: GetBookingQueryReturnType,
  setIsLoading: (arg: boolean) => void
  setWholePrice?: (arg?: number) => void
  setIsSubscriptionAllocable: (arg: boolean) => void
}> = ({setIsLoading, booking, setWholePrice, setIsSubscriptionAllocable}) => {
  const {openModal, closeModal} = useModalActions()
  const {l} = useLocale()
  const {venueId} = useParams()
  const {isLoggedIn, signIn} = useAuthState(venueId)
  const params = useParams<{venueId: string}>()
  const {data: venueData} = useQuery(
    VenueQueries.getVenueQuery({
      params: {id: params.venueId},
    }),
  )
  if (!booking) {
    return null
  }
  const {isLoading} = useBookingLoader()
  const {setFieldValue, values} = useFormikContext<CreateServiceBookingFormType>()

  const {
    data: validatedSubscriptions,
    mutate,
    // isLoading: isSubscriptionsLoading,
  } = useMutation(SubscriptionsQueries.validateUserSubscriptionsMutation())
  // Only `memberships` are ever offered or auto-attached; `unavailable` passes are shown, greyed out, in the chooser.
  const subscriptions = validatedSubscriptions?.memberships
  const unavailableSubscriptions = validatedSubscriptions?.unavailable
  const passHoursOptions = usePassHoursOptions()
  useEffect(() => {
    if (!venueData) {
      return
    }
    mutate({
      options: [
        {
          date: booking.date,
          venueId: venueData.uid,
          isConsideredOption: true,
          areaId: booking.area?.uid,
          duration: booking.duration,
          sportId: booking.sport?.uid,
          teacherId: booking.teacherId,
          userId: auth.currentUser?.uid,
          consideredDate: booking.date,
          sportType: booking.sportType,
          useForParticipants: values.useForParticipants,
          bookingType: booking.placeEntity === 'teacher' ? 'TEACHER' : 'SPORT',
        },
      ],
    })
  }, [venueData?.uid, auth.currentUser?.uid])
  const {data: user} = useQuery(UserService.queries.getUserQuery({cacheTime: 0, params: {id: auth.currentUser?.uid}}))
  const {data: member} = useQuery(
    MemberQueries.getMemberByVenueQuery({
      query: {
        venueId: venueData?.uid,
        userId: user?.uid,
      },
    }),
  )
  useEffect(() => {
    const subscription = subscriptions?.filter(el => !el.disabledFromAssessConfig)[0]
    if (values.participants.length === 0) {
      return
    }
    const firstParticipant = values.participants[0]
    if (subscriptions && subscriptions.length > 0 && subscription) {
      // WBA-4727: compare like with like — `membership.uid` is the membership PRODUCT id and
      // `subscription.uid` is a usageLimits doc id, so this guard never held and the effect
      // overwrote a manually chosen subscription every time validate/byUser re-resolved.
      if (firstParticipant.membership?.membershipBookingId === subscription.membershipBookingId) {
        return
      }
      setIsSubscriptionAllocable(true)
      setFieldValue('participants.0', {
        ...firstParticipant,
        name: user?.name ? user.name : firstParticipant.name,
        memberParticipantIndex: 0,
        membershipId: subscription.membershipId,
        coverEquipment: subscription.coverEquipment,
        membershipBookingId: subscription.membershipBookingId,
        membership: {
          logo: subscription.logo,
          uid: subscription.membershipId,
          name: subscription.membershipName,
          cancelPolicy: subscription.cancelPolicy,
          activityGroupId: subscription.activityGroupId,
          membershipBookingId: subscription.membershipBookingId,
          membershipBookingReference: subscription.membershipBookingReference,
        },
      })
    } else {

      setFieldValue('participants.0', {
        ...firstParticipant,
        name: user?.name ? user.name : firstParticipant.name,
        memberParticipantIndex: 0,
      })
    }
  }, [subscriptions, setFieldValue, isLoggedIn])
  const queryData: CalculateSplitBookingPriceQueryParams = useMemo(
    () => ({
      date: booking.date,
      bookingId: booking.uid,
      venueId: venueData?.uid,
      teacherId: booking.teacherId,
      userId: auth.currentUser?.uid,
      duration: booking.duration || 0,
      participants: values.participants,
      sportId: booking.sport?.uid || '',
      splitPayment: values.splitPayment,
      start: Number(booking.date_timestamp) || 0,
      useForParticipants: values.useForParticipants,
      bookingType: booking.placeEntity === 'teacher' ? 'TEACHER' : 'SPORT',
    }),
    [venueData, values, auth.currentUser?.uid],
  )
  const {data, isFetching, refetch} = useQuery(
    BookingQueries.calculateSplitBookingPriceQuery({
      enabled: !!values.participants,
      query: queryData,
      refetchOnWindowFocus: false,
    }),
  )
  useEffect(() => {
    if (queryData.venueId) {
    // if (queryData.venueId && queryData.userId) { // NOTE: queryData.userId is not used in the query, have to remove this condition
      refetch()
    }
  }, [queryData.venueId, queryData.userId, values.participants, values.useForParticipants])
  const wholeDiscount = useMemo(() => data?.discount ?? 0, [data])
  const initialDiscount = useMemo(() => data?.initialDiscount ?? 0, [data])
  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching, setIsLoading])
  useEffect(() => {
    setWholePrice && setWholePrice(data?.price)
  }, [isFetching, data, setWholePrice])

  const myParticipants: MemberParticipantType[] = useMemo(
    () => [
      {
        name: user?.name || `${user?.firstName} ${user?.lastName}`,
        avatar: {
          secure_url: user?.avatar || NO_USER_PHOTO,
        },
      },
      ...(member?.participants || []),
    ],
    [member?.participants, user],
  )

  const handleEditParticipant = useCallback(
    (index: number) => {
      const participants = values.participants
      const handleSetParticipant = ({
        name,
        subscription,
        memberParticipantIndex,
        avatar,
      }: {
        avatar: string | null
        name: string
        subscription?: Subscription
        memberParticipantIndex: number | null
      }) => {
        const participantsToSave = [...participants]
        // const canSetSubscriptionAllocable = booking.sport?.splitPayment || booking.sport?.multiplyPerPerson
        // if (index === 0) {
        //   setIsSubscriptionAllocable(
        //     (subscription?.useForParticipants && canSetSubscriptionAllocable) ||
        //       false,
        //   )
        //   if (participants[index].membershipId !== subscription?.membershipId) {
        //     setIsSubscriptionShared(
        //       subscription?.useForParticipants && canSetSubscriptionAllocable,
        //     )
        //   }
        // }
        participantsToSave[index] = {
          ...participantsToSave[index],
          name,
          memberParticipantIndex,
          // WBA-4727: `uid` here is the usageLimits doc id, not the subscription booking.
          membershipBookingId: subscription?.membershipBookingId,
          membershipId: subscription?.membershipId,
          coverEquipment: subscription?.coverEquipment,
          avatar: avatar || participantsToSave[index]?.avatar || NO_USER_PHOTO,
          membership: subscription
            ? {
                logo: subscription.logo,
                uid: subscription.membershipId,
                name: subscription.membershipName,
                cancelPolicy: subscription.cancelPolicy,
                membershipBookingId: subscription.membershipBookingId,
                membershipBookingReference: subscription.membershipBookingReference,
              }
            : null,
        }
        setFieldValue('participants', participantsToSave)
        closeModal()
      }
      openModal({
        component: (
          <EditParticipantForm
            handleSubmit={handleSetParticipant as EditParticipantFormProps['handleSubmit']}
            initialName={participants[index].name || ''}
            initialSubscriptionBookingId={isLoggedIn ? participants[index].membership?.membershipBookingId : undefined}
            subscriptions={isLoggedIn ? subscriptions : []}
            initialMemberParticipantIndex={
              participants[index].memberParticipantIndex || participants[index].memberParticipantIndex === 0
                ? participants[index].memberParticipantIndex
                : null
            }
          >
            <EditParticipantModal
              isLoggedIn={isLoggedIn}
              allowedSubscriptionsList={booking.sport?.accessConfig?.subscriptions}
              initialMemberParticipantIndex={
                participants[index].memberParticipantIndex || participants[index].memberParticipantIndex === 0
                  ? participants[index].memberParticipantIndex
                  : null
              }
              anySubscriptions={booking.sport?.accessForAnyone || booking.sport?.accessConfig?.anySubscriptions}
              subscriptions={isLoggedIn && index === 0 ? subscriptions : undefined}
              unavailableSubscriptions={isLoggedIn && index === 0 ? unavailableSubscriptions : undefined}
              memberParticipants={myParticipants}
              omittedMemberParticipants={participants.reduce((acc: number[], cur) => {
                if (
                  (cur.memberParticipantIndex || cur.memberParticipantIndex === 0) &&
                  participants[index].memberParticipantIndex !== cur.memberParticipantIndex
                ) {
                  return [...acc, cur.memberParticipantIndex]
                }
                return acc
              }, [])}
            />
          </EditParticipantForm>
        ),
        title: participants[index].name,
        footerConfig: {
          disabled: true,
        },
      })
    },
    [l, values.participants, openModal, closeModal, subscriptions, unavailableSubscriptions],
  )

  const bookingPriceData = useMemo(() => {
    if (!data) {
      return []
    }
    // const equipments = getParticipantsEquipment(data.participants)
    const participantsData: PricingElementType[] = []
    const timeArr: string[] = []
    if (data.duration.hours) {
      timeArr.push(l('hours_short', {value: data.duration.hours}))
    }
      if (data.duration.minutes) {
        timeArr.push(l('minutes_short', {value: data.duration.minutes}))
      }
      const priceAndDiscount = data.participants.reduce(
        (acc2, participant) => ({
          price: acc2.price + participant.totalPrice + participant.discount + (participant.initialDiscount || 0),
          discount: acc2.discount + participant.discount,
        }),
        {
          price: 0,
          discount: 0,
        }
      )
      if (values.splitPayment === true) {
        data.participants.forEach((participant, index) => {
          const participantLine = {
          discount: participant.discount,
          count: 0,
          id: `participants.${index}.toPay`,
          withCheck: true,
          onCheckChange: () => {
            setFieldValue(`participants.${index}.toPay`, !participant.toPay)
          },
          subtext: <Flex alignItems='center'>
            {participant.name}
            {
              ((!isLoggedIn && index === 0)
              || participant.membershipName)
              && <Divider styleType='dot' />
            }
            {
              !isLoggedIn && index === 0 && (
                <Button settings={{height: '20px', icon: {height: '20px', iconHeight: '20px'}}} icon='plusSmall' type='flat_short' onClick={() => signIn()}>
                  Use Subscription
                </Button>
              )
            }
            {
              participant.membershipName
              ? (<Flex maxWidth={['200px', '400px']}>
                    <IconWithText
                      icon='crownAction'
                      text={participant.membershipName}
                      settings={{icon: {color: '#FF9E00'}, text: {color: '#FF9E00'}}}
                    />
                </Flex>) : null
            }
            {
              participant.membershipName || (index === 0 && subscriptions)
              ? (
                <Button
                  type='icon'
                  icon='edit'
                  settings={{
                    height: '20px',
                    m: '0 0 0 8px'
                  }}
                  onClick={() => handleEditParticipant(index)}
                  disabled={isLoading}
                />) : null
            }
            </Flex>,
          checked: participant.toPay,
          price: participant.activityPrice,
          text: <Flex>{(participant.index || index)+1}/{booking.participants.length}<Icon icon='user' /> {l('service_price_element', {time: timeArr.join(' '), service: booking.sport?.name})}</Flex>,
        }
        const equipmentsLine = getParticipantsEquipment([participant], true, true)
        participantsData.push(...[participantLine, ...equipmentsLine])
      })
      } else {
        const equipments = getParticipantsEquipment(data.participants)
        participantsData.push(...[{
          count: data.numberOfPeople,
          price: priceAndDiscount.price,
          discount: priceAndDiscount.discount,
          text: l('service_price_element', {time: timeArr.join(' '), service: values.service?.name}),
        }, ...equipments])
      }
    return [
      ...participantsData,
    ]
  }, [data, l, values])
  console.log('====> ~ useEffect ~ data:', data)
  const totalPrice = useMemo(
    () => {
      return data?.price || data?.participants.reduce((acc, el) => acc + Number(el.totalPrice), 0)
    },
    [data],
  )

  return (
    <Box>
      <PricingDetails
        total={totalPrice}
        isLoading={isFetching}
        discount={wholeDiscount}
        isProcessing={isLoading}
        values={bookingPriceData}
        withDividerBetweenItems={true}
        initialDiscount={initialDiscount}
        withoutCurrency={false}
        currency={venueData?.currency ?? ''}
        predefinedLength={values.splitPayment === true ? values.participants.length : 1}
      />
      {isLoggedIn &&
        unavailableSubscriptions?.map((pass) => {
          const hours = formatOpeningTimes(pass.openingTimes, passHoursOptions)
          return (
            <Box
              mt={'8px'}
              key={`unavailable-${pass.membershipBookingId || pass.membershipId}`}
              data-testid='unavailable-pass-note'
            >
              <P
                fontSize={'tag'}
                color={'text-secondary'}
              >
                {l('pass_unavailable_summary', {name: pass.name})}
                {hours ? ` ${l('pass_usable_on')}: ${hours}` : ''}
              </P>
            </Box>
          )
        })}
    </Box>
  )
}
